import React, { useImperativeHandle, useRef, useState } from "react";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  ChartOptions,
  ChartData,
  BarElement,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import zoomPlugin from "chartjs-plugin-zoom";
import resetZoomIcon from "../../../images/resetZoom.svg";
import AnnotationLegend from "./annotationLegends";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  BarElement,
  annotationPlugin,
  zoomPlugin
);

export interface ChartAnnotation {
  type: "line";
  borderColor: string;
  borderWidth: number;
  value: string;
  scaleID: string;
  label: {
    backgroundColor: string;
    display: boolean;
    color: string;
    content: string;
    rotation?: number;
  };
}

export interface TooltipData {
  weightedAveragePromotionSales: number;
  maxDiscount: number;
  promoDiscount: number;
  minDiscount: number;
  periodWeekNumber: string;
  historicalShipment?: number;
  statisticalForecast?: number;
}

interface LinechartProps {
  chartRef: React.RefObject<any>;
  chartData: ChartData<"line" | "scatter" | "bar", { x: string; y: number }[]>;
  chartAnnotations: ChartAnnotation[];
  labels: string[];
  customTooltipData: TooltipData[];
}

const Linechart: React.FC<LinechartProps> = ({
  chartRef,
  chartData,
  chartAnnotations,
  labels,
  customTooltipData,
}) => {
  const internalChartRef = useRef<any>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [visibleAnnotations, setVisibleAnnotations] = useState<{
    [key: string]: boolean;
  }>({});

  useImperativeHandle(chartRef, () => ({
    resetZoom: () => {
      internalChartRef.current?.resetZoom();
      setIsZoomed(false);
    },
  }));

  const filteredChartData = {
    ...chartData,
    datasets: chartData.datasets?.map((dataset) => ({
      ...dataset,
      data: dataset.data.filter((point) => point.y >= 0),
      order: dataset.label === "Weighted Promo Discount" ? 1 : 10,
    })),
  };

  // Function to handle tooltip visibility for annotations with multiple entries
  const handleAnnotationTooltip = (tooltipItems: any[]) => {
    const tooltipItem = tooltipItems[0];

    // Check if it's an annotation (e.g., "line" type)
    if (tooltipItem?.datasetIndex === undefined) return [];

    // Look for annotations at the same position (i.e., same x value)
    const annotationsAtSamePosition = chartAnnotations.filter(
      (annotation) => annotation.value === tooltipItem.label
    );

    // If multiple annotations exist at the same position, return their tooltips
    if (annotationsAtSamePosition.length > 1) {
      return annotationsAtSamePosition.map((annotation) => {
        return `${annotation.value}: ${annotation.label.content.replace(
          /\s*\(.*?\)$/,
          ""
        )}`;
      });
    }

    return [];
  };

  const options: ChartOptions<"line" | "scatter" | "bar"> = {
    scales: {
      x: {
        type: "category",
        labels: labels,
        title: {
          display: true,
          color: "#FFFFFF",
        },
        ticks: {
          color: "#FFFFFF",
        },
      },
      y: {
        type: "linear",
        title: {
          display: true,
          text: "Volume",
          color: "#FFFFFF",
        },
        ticks: {
          color: "#FFFFFF",
        },
      },
      y1: {
        type: "linear",
        position: "right",
        min: 0,
        title: {
          display: true,
          text: "Percentage",
          color: "#FFFFFF",
        },
        ticks: {
          color: "#FFFFFF",
          stepSize: 1,
          callback: (value) => `${Number(value).toFixed(0)}%`,
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const dataset =
              filteredChartData.datasets[tooltipItems[0]?.datasetIndex];
            if (dataset?.label === "Previous Year Shipments") return "";
            return tooltipItems[0]?.label || ""; // Use default label for others
          },
          label: (tooltipItem) => {
            const dataset =
              filteredChartData.datasets[tooltipItem.datasetIndex];
            const dataPoint = dataset?.data[tooltipItem.dataIndex];
            const tooltipData = customTooltipData[tooltipItem.dataIndex];
            if (dataset?.label === "Weighted Promo Discount" && tooltipData) {
              const {
                maxDiscount,
                promoDiscount,
                weightedAveragePromotionSales,
                periodWeekNumber,
              } = tooltipData;
              return [
                `${periodWeekNumber} Weighted Average Discount: ${Math.trunc(
                  weightedAveragePromotionSales
                )}%`,
                `${Math.trunc(promoDiscount)}% of Volume`,
                `Max Discount: ${Math.trunc(maxDiscount)}%`,
              ];
            }

            if (dataset?.label === "Statistical Forecast") {
              return `Statistical Forecast: ${Math.trunc(dataPoint?.y)}`;
            }

            if (dataset?.label === "Historical Shipments (Adjusted)") {
              return `Historical Shipment: ${Math.trunc(dataPoint?.y)}`;
            }

            if (dataset?.label === "Previous Year Shipments") {
              return `Previous Year Volume: ${Math.trunc(dataPoint?.y)}`;
            }

            return `(${dataPoint?.x}, ${Math.trunc(dataPoint?.y)})`;
          },

          afterBody: (tooltipItems) => {
            const annotationTooltips = handleAnnotationTooltip(tooltipItems);
            if (annotationTooltips.length > 0) {
              return annotationTooltips;
            }
            return undefined;
          },
        },
        backgroundColor: "#000000",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        borderColor: "rgba(255,255,255,0.5)",
        borderWidth: 1,
        displayColors: true,
      },

      legend: {
        position: "top",
        align: "start",
        labels: {
          color: "#FFFFFF",
          usePointStyle: true,
        },
      },
      annotation: {
        annotations: chartAnnotations
          ?.filter(
            (annotation) => visibleAnnotations[annotation.value] !== false
          )
          ?.map((annotation) => ({
            ...annotation,
            label: {
              color: "#FFFFFF",
              display: true,
              rotation: 90,
              font: { size: 12 },
            },
          })),
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
            speed: 0.1,
          },
          pinch: {
            enabled: true,
          },
          mode: "xy",
          onZoom: () => {
            setIsZoomed(true);
          },
        },
        pan: {
          enabled: true,
          mode: "xy",
          onPan: () => {
            setIsZoomed(true);
          },
        },
        limits: {
          x: { min: "original", max: "original" },
          y: { min: "original", max: "original" },
        },
      },
    },
  };

  const chartContainerStyle: React.CSSProperties = {
    width: "90%",
    height: "380px",
  };

  return (
    <div
      style={chartContainerStyle}
      className="flex flex-col space-y-3 relative"
    >
      <Chart
        ref={internalChartRef}
        type="line"
        className="m-2 p-2"
        options={options}
        data={filteredChartData}
      />
      <AnnotationLegend
        legendItems={chartAnnotations}
        labelOrder={labels}
        onVisibilityChange={setVisibleAnnotations}
      />
      {isZoomed && (
        <button
          onClick={() => {
            internalChartRef.current?.resetZoom();
            setIsZoomed(false);
          }}
          className="absolute right-2"
        >
          <img src={resetZoomIcon} alt="reset zoom" />
        </button>
      )}
    </div>
  );
};

export default Linechart;
